import React, { useRef, useState } from 'react';
import { Line, Group, Text } from 'react-konva';
import isEqual from 'lodash/isEqual';

import {
    getPointsOnLine,
    getPointOnLine,
    getNormalSlope,
    generateLineFromSlopeAndPoint,
    distance,
} from 'src/util/math';
import { useDragGesture } from 'src/hooks/useDragGesture';
import Grabber from '../canvas/grabber';
import type { KonvaEventObject } from 'konva/lib/Node';

interface Point {
    x: number;
    y: number;
}

interface Props {
    scale: number;
    point1: Point;
    point2: Point;
    segments: number;
    ticks?: number[];
    offset: Point;
    hidden?: boolean;
    selected: boolean;
    cursorChange: (cursor: string) => void;
    updatePosition: (data: { point1?: Point; point2?: Point }) => void;
    selectRuler: () => void;
    drawing?: boolean;
    color?: string;
    // Passed down by `Rulers` but never read here -- kept optional/unused to match the original's
    // untyped PropTypes, which didn't declare it either.
    container?: HTMLElement | null;
}

interface DragData {
    startX: number;
    startY: number;
    clientX: number;
    clientY: number;
    point1OffsetX: number;
    point2OffsetX: number;
    point1OffsetY: number;
    point2OffsetY: number;
    draggingPoint: number;
}

// This is an expensive component to render (lots of tick-mark math), so it keeps the custom
// `shouldComponentUpdate`-style bail-out below rather than re-rendering on every parent render.
const Ruler: React.FC<Props> = ({
    point1,
    point2,
    cursorChange,
    offset,
    scale,
    segments,
    selected,
    drawing,
    color,
    ticks = [],
    hidden,
    updatePosition,
    selectRuler,
}) => {
    const [translating, setTranslating] = useState(false);

    // Snapshot-at-drag-start bookkeeping that render() never reads directly and that can't be
    // derived from the current render (it represents "where the gesture started", not "what's
    // true now") -- stays in a plain ref rather than state so it doesn't trigger re-renders of its
    // own. Everything else these gestures need (`point1`/`point2`/`scale`/`updatePosition`/...) is
    // read directly from props, since `useDragGesture` already guarantees its `onDragMove`/
    // `onDragEnd` callbacks are always the latest ones passed to it.
    const dragRef = useRef<DragData>({
        startX: point2.x,
        startY: point2.y,
        clientX: 0,
        clientY: 0,
        point1OffsetX: 0,
        point2OffsetX: 0,
        point1OffsetY: 0,
        point2OffsetY: 0,
        draggingPoint: 2,
    });

    const handleDragMove = (e: Event) => {
        const evt = e as MouseEvent;
        let { clientX, clientY } = dragRef.current;
        if (!clientX && !clientY) {
            clientX = evt.clientX;
            clientY = evt.clientY;
            dragRef.current.clientX = clientX;
            dragRef.current.clientY = clientY;
        }
        const { draggingPoint, startX, startY } = dragRef.current;
        const point = draggingPoint === 1 ? 'point1' : 'point2';
        const newPoint = {
            x: startX - (clientX - evt.clientX) / scale,
            y: startY - (clientY - evt.clientY) / scale,
        };
        updatePosition({ [point]: newPoint });
    };

    const { start: startDrag } = useDragGesture({ onDragMove: handleDragMove });

    const onDragStart = (index: number) => (e: KonvaEventObject<MouseEvent>) => {
        if (e) {
            const point = index === 1 ? point1 : point2;
            dragRef.current.draggingPoint = index;
            dragRef.current.clientX = e.evt.clientX;
            dragRef.current.clientY = e.evt.clientY;
            dragRef.current.startX = point.x;
            dragRef.current.startY = point.y;
            startDrag();
            e.cancelBubble = true;
        }
    };

    const handleTranslateMove = (e: Event) => {
        const evt = e as MouseEvent;
        const { point1OffsetX, point2OffsetX, point1OffsetY, point2OffsetY } = dragRef.current;
        const newPoint1 = { x: evt.clientX / scale - point1OffsetX, y: evt.clientY / scale - point1OffsetY };
        const newPoint2 = { x: evt.clientX / scale - point2OffsetX, y: evt.clientY / scale - point2OffsetY };
        updatePosition({ point1: newPoint1, point2: newPoint2 });
    };

    const { start: startTranslate } = useDragGesture({
        onDragMove: handleTranslateMove,
        onDragEnd: () => setTranslating(false),
    });

    const onTranslateStart = (e: KonvaEventObject<MouseEvent>) => {
        dragRef.current.point1OffsetX = e.evt.clientX / scale - point1.x;
        dragRef.current.point2OffsetX = e.evt.clientX / scale - point2.x;
        dragRef.current.point1OffsetY = e.evt.clientY / scale - point1.y;
        dragRef.current.point2OffsetY = e.evt.clientY / scale - point2.y;
        startTranslate();
        setTranslating(true);
        e.cancelBubble = true;

        if (!selected) {
            selectRuler();
        }
    };

    if (hidden) {
        return null;
    }

    const scaledPoint1 = { x: point1.x * scale + offset.x, y: point1.y * scale + offset.y };
    const scaledPoint2 = { x: point2.x * scale + offset.x, y: point2.y * scale + offset.y };
    const pointArray = [scaledPoint1.x, scaledPoint1.y, scaledPoint2.x, scaledPoint2.y];

    let totalTicks = segments - 1;
    let halfTicks = false;
    let tenthTicks = false;
    const spacePerSegment = distance(scaledPoint1, scaledPoint2) / segments;
    if (spacePerSegment > 30) {
        tenthTicks = true;
        totalTicks = segments * 10 - 1;
    } else if (spacePerSegment > 5) {
        halfTicks = true;
        totalTicks = segments * 2 - 1;
    }
    const regularTicks = getPointsOnLine(scaledPoint1, scaledPoint2, totalTicks);
    const normalSlope = getNormalSlope(scaledPoint1, scaledPoint2);

    const stroke = drawing ? 'red' : color;

    function drawTick(tick: Point, key: string, length: number, tickColor?: string) {
        const points = generateLineFromSlopeAndPoint(tick, normalSlope, length);
        return (
            <Line
                key={key}
                points={[points[0].x, points[0].y, points[1].x, points[1].y]}
                stroke={tickColor}
                strokeWidth={1}
            />
        );
    }

    return (
        <Group>
            <Line points={pointArray} stroke={stroke} strokeWidth={1} />
            <Line points={pointArray} stroke='transparent' strokeWidth={10} onMouseDown={onTranslateStart} />
            {regularTicks.map((point, index) => {
                let length = 7;
                if (halfTicks && index % 2 !== 1) {
                    length = 5;
                } else if (tenthTicks && index % 10 === 4) {
                    length = 5;
                } else if (tenthTicks && index % 10 !== 9) {
                    length = 3;
                }
                return drawTick(point, `reg-${index}`, length, stroke);
            })}
            {ticks.map((tick, index) => {
                const point = getPointOnLine(scaledPoint1, scaledPoint2, tick / segments);
                let labelPoint = { x: point.x + 10, y: point.y - 10 };
                if (normalSlope < -0.5 || normalSlope > 1.5) {
                    labelPoint = { x: point.x - 5, y: point.y + 10 };
                }
                /* eslint-disable react/no-array-index-key */
                return (
                    <Group key={`group-${index}`}>
                        {drawTick(point, `cust-${index}`, 9, 'red')}
                        <Text
                            x={labelPoint.x}
                            y={labelPoint.y}
                            text={`${tick}`}
                            fontSize={20}
                            fontFamily='Arial'
                            fill='red'
                            align='center'
                        />
                    </Group>
                );
                /* eslint-eable react/no-array-index-key */
            })}
            {!translating && selected && (
                <Group>
                    <Grabber position={scaledPoint1} cursorChange={cursorChange} onDragStart={onDragStart(1)} />
                    <Grabber position={scaledPoint2} cursorChange={cursorChange} onDragStart={onDragStart(2)} />
                </Group>
            )}
        </Group>
    );
};

// Equivalent to the original class's custom `shouldComponentUpdate`: always re-render while this or
// the next render is "drawing" (the live preview instance), otherwise only when one of the listed
// props actually changed. The one deliberate, documented difference from the original: there's no
// access to internal `translating` state here (React.memo's comparator only ever sees props), so the
// "always re-render while translating" branch isn't replicated -- it's a no-op in practice anyway,
// since translating a ruler always goes through `updatePosition`, which changes `point1`/`point2`
// (already covered by the prop comparison below) on every step of the gesture.
const propsToCompare = [
    'point1',
    'point2',
    'segments',
    'scale',
    'offset',
    'selected',
    'color',
    'ticks',
    'hidden',
] as const;

export default React.memo(Ruler, (prevProps, nextProps) => {
    if (prevProps.drawing || nextProps.drawing) {
        return false;
    }
    return propsToCompare.every((key) => isEqual(prevProps[key], nextProps[key]));
});
